/**
 * 한국 전화번호 포맷팅 유틸리티
 * 
 * 지원 형식:
 * - 지역번호 2자리: 02-123-4567, 02-1234-5678
 * - 지역번호 3자리: 031-123-4567, 031-1234-5678
 * - 휴대폰: 010-1234-5678, 011-1234-5678
 * - 특수번호: 070-1234-5678
 * - 국제 형식: +821012345678 → 010-1234-5678
 */

/**
 * 전화번호를 한국 형식으로 포맷팅합니다.
 * 
 * @param phoneNumber - 포맷팅할 전화번호 (숫자와 하이픈 포함 가능)
 * @returns 포맷팅된 전화번호 (예: 02-123-4567, 010-1234-5678, 070-1234-5678)
 * 
 * @example
 * formatPhoneNumber('01012345678') // '010-1234-5678'
 * formatPhoneNumber('0212345678') // '02-1234-5678'
 * formatPhoneNumber('02-123-4567') // '02-123-4567'
 * formatPhoneNumber('+821012345678') // '010-1234-5678'
 * formatPhoneNumber('+82212345678') // '02-1234-5678'
 */
export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) return '';

  // 국제 형식 (+82...) 감지 및 변환
  if (phoneNumber.startsWith('+82')) {
    // +82 제거 후 0 추가하여 한국 형식으로 변환
    // +821012345678 → 01012345678
    const withoutCountryCode = '0' + phoneNumber.slice(3);
    return formatPhoneNumber(withoutCountryCode);
  }

  // 숫자만 추출
  const numbers = phoneNumber.replace(/[^\d]/g, '');

  if (numbers.length === 0) return '';

  // 02로 시작하는 경우 (서울)
  if (numbers.startsWith('02')) {
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    if (numbers.length <= 9) {
      // 7자리: 02-123-4567
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
    }
    // 8자리 이상: 02-1234-5678
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
  }

  // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
  if (numbers.startsWith('010') || numbers.startsWith('011') || 
      numbers.startsWith('016') || numbers.startsWith('017') || 
      numbers.startsWith('018') || numbers.startsWith('019')) {
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    // 8자리 이상: 010-1234-5678
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  // 특수번호 (070, 0505, 0507)
  if (numbers.startsWith('070')) {
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    // 8자리 이상: 070-1234-5678
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  }

  if (numbers.startsWith('0505') || numbers.startsWith('0507')) {
    if (numbers.length <= 4) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    // 0505-123-4567
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 7)}-${numbers.slice(7, 11)}`;
  }

  // 지역번호 3자리 (031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  if (numbers.length <= 9) {
    // 7자리: 031-123-4567
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 9)}`;
  }
  // 8자리 이상: 031-1234-5678
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
}

/**
 * 전화번호 입력 중 포맷팅을 적용합니다.
 * 사용자가 입력하는 동안 실시간으로 포맷팅을 적용합니다.
 * 
 * @param value - 입력 중인 전화번호
 * @returns 포맷팅된 전화번호
 */
export function formatPhoneNumberInput(value: string): string {
  return formatPhoneNumber(value);
}

/**
 * 서버에 전송하기 전 전화번호를 정규화합니다.
 * 현재는 포맷팅된 형식 그대로 전송합니다.
 * 
 * @param phoneNumber - 포맷팅된 전화번호
 * @returns 서버에 전송할 전화번호 (포맷팅된 형식)
 */
export function normalizePhoneNumberForServer(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber) return null;
  const formatted = formatPhoneNumber(phoneNumber);
  return formatted || null;
}

/**
 * 서버에서 받은 전화번호를 표시용으로 포맷팅합니다.
 * 
 * @param phoneNumber - 서버에서 받은 전화번호
 * @returns 표시용 포맷팅된 전화번호
 */
export function formatPhoneNumberForDisplay(phoneNumber: string | null | undefined): string {
  return formatPhoneNumber(phoneNumber) || '-';
}


